/**
 * 팀 프로필 조회 관련 함수들
 */

import { createBrowserClient } from '@/supabase'
import { isTeamManager } from '@/utils/team-role'
import {
  getAuthenticatedUserAndProfile,
  getTeamMemberAccountsBatch,
  TEAM_MEMBER_STATUS,
  logTeamService,
} from '@/utils/team-helpers'
import type { Team, TeamDetail, TeamMember, TeamProfileWithMembers } from '../team-types'

/**
 * 매니저인 팀 목록 조회 (여러 팀 가능)
 * @deprecated fetchMyTeams를 사용하세요. 이 함수는 여러 팀을 반환하도록 수정되었지만, fetchMyTeams가 더 완전한 정보를 제공합니다.
 */
export const fetchTeamProfileByTeamManager = async () => {
  try {
    const { supabase, profileId } = await getAuthenticatedUserAndProfile()

    // manager_profile_id를 사용하여 팀 조회 (여러 팀 가능, 삭제되지 않은 팀만)
    const { data: teamsData, error } = await supabase
      .from('teams')
      .select('*')
      .eq('manager_profile_id', profileId)
      .is('deleted_at', null)

    if (error) {
      return { data: null, error }
    }

    if (!teamsData || teamsData.length === 0) {
      return { data: null, error: null }
    }

    // 여러 팀이 있을 수 있으므로 첫 번째 팀만 반환 (하위 호환성)
    // 여러 팀을 모두 반환하려면 fetchMyTeams를 사용하세요
    const team = (teamsData[0] as any) as Team

    // 매니저 정보 조회
    const { data: managerData } = await supabase
      .from('accounts')
      .select('profile_id, user_id, username, bio')
      .eq('profile_id', team.manager_profile_id as string)
      .maybeSingle()

    // 팀원 정보 조회 (MEMBER 역할만)
    const { data: teamMembers } = await supabase
      .from('team_members')
      .select('*')
      .eq('team_id', team.id)
      .eq('status', TEAM_MEMBER_STATUS.ACTIVE)
      .eq('role', 'MEMBER')

    interface TeamMemberRow {
      id: number
      team_id: number | null
      profile_id: string | null
      status: string | null
      [key: string]: any
    }
    const teamMembersTyped = (teamMembers || []) as unknown as TeamMemberRow[]

    // 팀원들의 계정 정보 배치 조회
    const memberProfileIds = teamMembersTyped
      .map((m) => m.profile_id)
      .filter(Boolean) as string[]

    const accountsMap = await getTeamMemberAccountsBatch(supabase as any, memberProfileIds)

    const enrichedMembers = teamMembersTyped.map((member) => ({
      ...member,
      account: member.profile_id ? accountsMap.get(member.profile_id) || null : null,
    }))

    return {
      data: {
        ...team,
        manager: managerData || null,
        team_members: enrichedMembers,
      },
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('프리랜서 프로필을 찾을 수 없습니다.'),
    }
  }
}

export const fetchTeamProfile = async (teamId: string | number) => {
  const supabase = createBrowserClient()
  const normalizedTeamId: number = typeof teamId === 'string' ? Number(teamId) : teamId

  if (!Number.isFinite(normalizedTeamId)) {
    return { data: null, error: new Error('유효하지 않은 팀 ID입니다.') }
  }

  const { data, error } = await supabase
    .from('teams')
    .select(`*, team_members:team_members(*)`)
    .eq('id', normalizedTeamId)
    .is('deleted_at', null)
    .single()

  return { data, error }
}

/**
 * 특정 팀의 상세 정보 조회
 *
 * 기업 사용자와 프리랜서 사용자 모두 사용 가능합니다.
 * - 기업 사용자: profileId 없이도 팀 상세 정보 조회 가능
 * - 프리랜서 사용자: profileId 기반 부가 정보(매니저 여부 등) 제공
 */
export const fetchTeamDetail = async (teamId: number) => {
  try {
    const supabase = createBrowserClient()
    const { data: { user }, error: authError } = await supabase.auth.getUser()

    if (authError || !user) {
      return {
        data: null,
        error: new Error('로그인이 필요합니다.'),
      }
    }

    // 프로필 조회 시도 (기업 사용자는 프로필이 없을 수 있음)
    let profileId: string | null = null
    try {
      const { profileId: pid } = await getAuthenticatedUserAndProfile()
      profileId = pid ?? null
    } catch {
      // 기업 사용자는 accounts 프로필이 없을 수 있음 → 그냥 통과
      profileId = null
    }

    // 팀 상세 정보 조회
    const { data: team, error: teamError } = await supabase
      .from('teams')
      .select('*')
      .eq('id', teamId)
      .is('deleted_at', null)
      .maybeSingle()

    if (teamError) {
      return { data: null, error: teamError }
    }

    if (!team) {
      return { data: null, error: new Error('팀을 찾을 수 없습니다.') }
    }

    const teamTyped = team as unknown as Team

    // 매니저인지 확인 (profileId가 있을 때만)
    const isManager = profileId ? isTeamManager(teamTyped, profileId) : false

    // 매니저 정보 조회
    // 1순위: team_members에서 role='OWNER'인 멤버 조회 (우선)
    // 2순위: teams.manager_profile_id로 조회 (fallback)
    let managerData: { profile_id: string; user_id: string; username: string; bio?: string | null } | null = null
    
    // team_members에서 OWNER 역할인 멤버 찾기
    const { data: ownerMember } = await supabase
      .from('team_members')
      .select('profile_id')
      .eq('team_id', teamId)
      .eq('status', TEAM_MEMBER_STATUS.ACTIVE)
      .eq('role', 'OWNER')
      .is('deleted_at', null)
      .maybeSingle()
    
    if (ownerMember?.profile_id) {
      const { data } = await supabase
        .from('accounts')
        .select('profile_id, user_id, username, bio, main_job, skills, profile_image_url, profile_created_at, availability_status, contact_website')
        .eq('profile_id', ownerMember.profile_id)
        .is('deleted_at', null)
        .maybeSingle()
      managerData = (data as unknown as { 
        profile_id: string
        user_id: string
        username: string
        bio?: string | null
        main_job?: string[] | null
        skills?: string[] | null
        profile_image_url?: string | null
        profile_created_at?: string | null
        availability_status?: string | null
        contact_website?: string | null
      }) || null
    }
    
    // team_members의 OWNER로 조회 실패 시, teams.manager_profile_id로 fallback
    if (!managerData && teamTyped.manager_profile_id) {
      const { data } = await supabase
        .from('accounts')
        .select('profile_id, user_id, username, bio, main_job, skills, profile_image_url, profile_created_at, availability_status, contact_website')
        .eq('profile_id', teamTyped.manager_profile_id as string)
        .is('deleted_at', null)
        .maybeSingle()
      managerData = (data as unknown as { 
        profile_id: string
        user_id: string
        username: string
        bio?: string | null
        main_job?: string[] | null
        skills?: string[] | null
        profile_image_url?: string | null
        profile_created_at?: string | null
        availability_status?: string | null
        contact_website?: string | null
      }) || null
    }

    // 팀원 정보 조회 (MEMBER 역할만)
    const { data: teamMembers, error: teamMembersError } = await supabase
      .from('team_members')
      .select('*')
      .eq('team_id', teamId)
      .eq('status', TEAM_MEMBER_STATUS.ACTIVE)
      .eq('role', 'MEMBER')
      .is('deleted_at', null)

    if (teamMembersError) {
      logTeamService('warn', 'fetchTeamDetail', { error: teamMembersError, teamId })
    }

    interface TeamMemberRow2 {
      id: number
      team_id: number | null
      profile_id: string | null
      status: string | null
      [key: string]: any
    }
    const teamMembersTyped2 = (teamMembers || []) as unknown as TeamMemberRow2[]

    // 팀원들의 계정 정보 배치 조회 (성능 최적화)
    const memberProfileIds = teamMembersTyped2
      .map((m) => m.profile_id)
      .filter(Boolean) as string[]

    const accountsMap = await getTeamMemberAccountsBatch(supabase as any, memberProfileIds)

    const enrichedMembers = teamMembersTyped2.map((member) => ({
      ...member,
      account: member.profile_id ? accountsMap.get(member.profile_id) || null : null,
    })) as TeamMember[]

    const result: TeamDetail = {
      ...teamTyped,
      isManager,
      manager: managerData || null,
      team_members: enrichedMembers,
    }

    return {
      data: result,
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('팀 정보를 불러오는데 실패했습니다.'),
    }
  }
}

/**
 * 특정 팀의 프로필과 팀원 목록 조회
 */
export const fetchTeamProfileWithMembers = async (
  teamId: number,
): Promise<TeamProfileWithMembers> => {
  const supabase = createBrowserClient()

  // 1) 팀 기본 정보
  const { data: team, error: teamError } = await supabase
    .from('teams')
    .select(
      `
      id,
      name,
      bio,
      specialty,
      sub_specialty,
      prefered,
      manager_profile_id
    `,
    )
    .eq('id', teamId)
    .is('deleted_at', null)
    .maybeSingle()

  if (teamError) throw teamError
  if (!team) throw new Error('팀을 찾을 수 없습니다.')

  interface TeamBasicInfo2 {
    id: number
    name: string
    bio?: string | null
    specialty?: string[] | null
    sub_specialty?: string[] | null
    prefered?: string[] | null
    manager_profile_id: string | null
  }
  const teamTyped2 = team as unknown as TeamBasicInfo2

  // 2) 팀원 목록 (MEMBER 역할만)
  const { data: memberRows, error: memberError } = await supabase
    .from('team_members')
    .select('profile_id, status')
    .eq('team_id', teamId)
    .eq('role', 'MEMBER')

  if (memberError) throw memberError

  interface MemberRow {
    profile_id: string | null
    status: string | null
  }

  const memberProfileIds = Array.from(
    new Set(
      ((memberRows ?? []) as unknown as MemberRow[])
        .map((m) => m.profile_id)
        .filter((id): id is string => id !== null && id !== undefined),
    ),
  )

  // 매니저 + 팀원들 프로필 한 번에 조회
  const allProfileIds = [teamTyped2.manager_profile_id, ...memberProfileIds].filter(
    Boolean,
  ) as string[]

  let profilesById: Record<string, any> = {}
  if (allProfileIds.length > 0) {
    const { data: accounts, error: accountError } = await supabase
      .from('accounts')
      .select('profile_id, username, bio, main_job')
      .in('profile_id', allProfileIds)
      .is('deleted_at', null)

    if (accountError) throw accountError

    interface AccountRow {
      profile_id: string
      username: string
      bio?: string | null
      main_job?: string | null
    }
    profilesById = ((accounts ?? []) as unknown as AccountRow[]).reduce(
      (acc, cur) => ({
        ...acc,
        [cur.profile_id]: cur,
      }),
      {},
    )
  }

  const managerProfile =
    teamTyped2.manager_profile_id && profilesById[teamTyped2.manager_profile_id as string]

  const members = ((memberRows ?? []) as unknown as MemberRow[]).map((m) => ({
    profile_id: m.profile_id || '',
    status: m.status || '',
    username: m.profile_id ? profilesById[m.profile_id]?.username ?? null : null,
    bio: m.profile_id ? profilesById[m.profile_id]?.bio ?? null : null,
    main_job: m.profile_id ? profilesById[m.profile_id]?.main_job ?? null : null,
  }))

  const result: TeamProfileWithMembers = {
    id: teamTyped2.id,
    name: teamTyped2.name,
    bio: teamTyped2.bio,
    specialty: teamTyped2.specialty,
    sub_specialty: teamTyped2.sub_specialty,
    prefered: teamTyped2.prefered,
    manager: managerProfile
      ? {
          profile_id: managerProfile.profile_id,
          username: managerProfile.username,
          bio: managerProfile.bio,
          main_job: managerProfile.main_job,
        }
      : null,
    members,
  }

  return result
}

